/**
 * These named transformations are defined in Cloudinary at the following URL:
 * https://cloudinary.com/console/transformations
 * After logging in with credentials provided on 1password, select "Only named" in the filter dropdown.
 *
 * For information on named transfromations, see:
 * https://cloudinary.com/documentation/chained_and_named_transformations#named_transformations
 */
export enum HgCloudinaryNamedTransformation {
  WEB_100 = 't_web_100',
  WEB_320 = 't_web_320',
  WEB_500 = 't_web_500',
  SMALL = 't_web_dp_small', // 800px
  LARGE = 't_web_dp_large', // 1500px
  FULLSCREEN = 't_web_dp_fullscreen',
}

/**
 * Given a cloudinary url, computes the correct URL for a given transformation
 */
export function getCloudinaryUrlForTransformation(
  cloudinaryUrl: string | undefined = '',
  transformation: HgCloudinaryNamedTransformation,
): string {
  // handle for legacy IS24 cdn url
  if (isLegacyIs24CdnUrl(cloudinaryUrl)) {
    return cloudinaryUrl;
  }

  if (cloudinaryUrl.includes('image/upload')) {
    return cloudinaryUrl.replace('image/upload', `image/upload/f_auto/${transformation}`);
  }
  return cloudinaryUrl.replace(/(https?:\/\/.*?\/)/, `$1f_auto/${transformation}/`);
}

function isLegacyIs24CdnUrl(url: string): boolean {
  return url.startsWith(import.meta.env.VUE_IMMOSCOUT24_CDN_URL);
}

/**
 * Generates responsive image URLs for different screen sizes using Cloudinary transformations.
 * Returns a srcset string with URLs for small (800px), large (1500px), and fullscreen (2000px) versions.
 */
export function getCloudinaryUrlVersions(image: string) {
  return [
    {
      url: getCloudinaryUrlForTransformation(image, HgCloudinaryNamedTransformation.SMALL),
      width: 800,
    },
    {
      url: getCloudinaryUrlForTransformation(image, HgCloudinaryNamedTransformation.LARGE),
      width: 1500,
    },
    {
      url: getCloudinaryUrlForTransformation(image, HgCloudinaryNamedTransformation.FULLSCREEN),
      width: 2000,
    },
  ]
    .map((version) => `${version.url} ${version.width}w`)
    .join(', ');
}
